// PeptideVault v6.5 — Health + health docs

// ══════════════════════════════════════════════════════
// RENDER: HEALTH TAB
// ══════════════════════════════════════════════════════
const HEALTH_TYPES = {
  // Body composition
  weight:        { label:'Weight',            unit:'lb',   hk:'HKQuantityTypeIdentifierBodyMass' },
  bodyFat:       { label:'Body Fat %',        unit:'%',    hk:'HKQuantityTypeIdentifierBodyFatPercentage' },
  bmi:           { label:'BMI',               unit:'',     hk:'HKQuantityTypeIdentifierBodyMassIndex' },
  leanBodyMass:  { label:'Lean Body Mass',    unit:'lb',   hk:'HKQuantityTypeIdentifierLeanBodyMass' },
  // Cardiovascular
  heartRate:     { label:'Heart Rate',         unit:'bpm',  hk:'HKQuantityTypeIdentifierHeartRate' },
  rhr:           { label:'Resting HR',        unit:'bpm',  hk:'HKQuantityTypeIdentifierRestingHeartRate' },
  hrv:           { label:'HRV',               unit:'ms',   hk:'HKQuantityTypeIdentifierHeartRateVariabilitySDNN' },
  walkingHR:     { label:'Walking HR',        unit:'bpm',  hk:'HKQuantityTypeIdentifierWalkingHeartRateAverage' },
  bp_systolic:   { label:'Blood Pressure (S)',unit:'mmHg', hk:'HKQuantityTypeIdentifierBloodPressureSystolic' },
  bp_diastolic:  { label:'Blood Pressure (D)',unit:'mmHg', hk:'HKQuantityTypeIdentifierBloodPressureDiastolic' },
  spo2:          { label:'Blood Oxygen %',    unit:'%',    hk:'HKQuantityTypeIdentifierOxygenSaturation' },
  vo2max:        { label:'VO2 Max',           unit:'mL/kg/min', hk:'HKQuantityTypeIdentifierVO2Max' },
  cardioRecovery:{ label:'Cardio Recovery',   unit:'bpm',  hk:'HKQuantityTypeIdentifierHeartRateRecoveryOneMinute' },
  // Metabolic
  bloodGlucose:  { label:'Blood Glucose',     unit:'mg/dL',hk:'HKQuantityTypeIdentifierBloodGlucose' },
  restingEnergy: { label:'Resting Energy',    unit:'cal',  hk:'HKQuantityTypeIdentifierBasalEnergyBurned' },
  activeEnergy:  { label:'Active Energy',     unit:'cal',  hk:'HKQuantityTypeIdentifierActiveEnergyBurned' },
  // Sleep & Recovery
  sleep:         { label:'Sleep',             unit:'hrs',  hk:'HKCategoryTypeIdentifierSleepAnalysis' },
  sleepAwake:    { label:'Sleep — Awake',     unit:'hrs' },
  sleepREM:      { label:'Sleep — REM',       unit:'hrs' },
  sleepCore:     { label:'Sleep — Core',      unit:'hrs' },
  sleepDeep:     { label:'Sleep — Deep',      unit:'hrs' },
  respRate:      { label:'Respiratory Rate',  unit:'br/min',hk:'HKQuantityTypeIdentifierRespiratoryRate' },
  wristTemp:     { label:'Wrist Temp Δ',      unit:'°F',   hk:'HKQuantityTypeIdentifierAppleSleepingWristTemperature' },
  // Activity
  steps:         { label:'Steps',             unit:'steps',hk:'HKQuantityTypeIdentifierStepCount' },
  exerciseTime:  { label:'Exercise Minutes',  unit:'min',  hk:'HKQuantityTypeIdentifierAppleExerciseTime' },
  workoutTime:   { label:'Workout Duration',  unit:'min' },
  walkRunDistance:{label:'Walking + Running Distance',unit:'mi',hk:'HKQuantityTypeIdentifierDistanceWalkingRunning' },
  walkingSteadiness:{label:'Walking Steadiness',unit:'%',hk:'HKQuantityTypeIdentifierAppleWalkingSteadiness' },
  walkingSpeed:  { label:'Walking Speed',      unit:'mph',  hk:'HKQuantityTypeIdentifierWalkingSpeed' },
  stepLength:    { label:'Walking Step Length',unit:'in',   hk:'HKQuantityTypeIdentifierWalkingStepLength' },
}

function renderHealthTab() {
  const metric = S.healthMetric
  const today  = new Date(); today.setHours(23,59,59,999)
  const startDate = S.healthStart ? new Date(S.healthStart + 'T00:00:00') : null
  const endDate   = S.healthEnd   ? new Date(S.healthEnd   + 'T23:59:59') : today

  const metricData = S.healthData
    .filter(d => {
      if (d.type !== metric) return false
      const dt = new Date(d.date + 'T12:00:00')
      if (dt > today) return false
      if (startDate && dt < startDate) return false
      if (dt > endDate) return false
      return true
    })
    .sort((a,b) => a.date.localeCompare(b.date))

  const series = metricData.length ? [{
    label: HEALTH_TYPES[metric]?.label,
    color: var_blue(),
    points: metricData.map((d,i) => ({ x: i, y: +d.value }))
  }] : []

  const latest = metricData[metricData.length-1]
  const oldest = metricData[0]
  const trend  = latest && oldest && metricData.length > 1
    ? (+latest.value - +oldest.value).toFixed(1)
    : null

  // Preset shortcuts (same pattern as analytics)
  const presets = [['30','30d'],['90','90d'],['180','6m'],['365','1yr'],['','All']]
  function isPresetActive(days) {
    if (!days) return !S.healthStart && !S.healthEnd
    const s = localDateOffset(-days)
    return S.healthStart === s && !S.healthEnd
  }

  return `
  <div style="animation:fadeIn .25s ease">
    <div class="row" style="margin-bottom:16px;flex-wrap:wrap;gap:8px">
      <h2 style="font-size:20px;font-weight:700">Health Data</h2>
      <div class="spacer"></div>
      <button class="btn btn-primary btn-sm" onclick="importHealthXML()">${ICONS.plus} Import Apple Health XML</button>
      ${S.healthData.length ? `<button class="btn btn-danger btn-sm" onclick="clearHealthData()">${ICONS.trash}</button>` : ''}
    </div>

    ${S.healthData.length === 0 ? `
    <div class="card" style="margin-bottom:16px">
      <div class="card-body" style="background:var(--blueL);border-radius:var(--r2);padding:16px">
        <div style="font-size:14px;font-weight:600;color:var(--blue2);margin-bottom:8px">How to export from Apple Health:</div>
        <ol style="font-size:13px;color:var(--text2);padding-left:18px;line-height:1.8">
          <li>Open the <strong>Health app</strong> on your iPhone</li>
          <li>Tap your profile photo (top right)</li>
          <li>Scroll down → <strong>Export All Health Data</strong></li>
          <li>Tap <strong>Export</strong> and save/share the zip file</li>
          <li>Unzip it — you'll find <strong>export.xml</strong> inside</li>
          <li>Click <strong>Import Apple Health XML</strong> above and select that file</li>
        </ol>
      </div>
    </div>
    <div class="card">
      <div class="empty-state" style="padding:48px">
        <div class="empty-icon">❤️</div>
        <p>No health data imported yet</p>
        <span>Import your Apple Health export XML to track metrics alongside your peptide schedule</span>
      </div>
    </div>` : `

    <!-- Metric tabs grouped by category -->
    ${[
      { label: 'Body',         keys: ['weight','bodyFat','bmi','leanBodyMass'] },
      { label: 'Heart',        keys: ['heartRate','rhr','hrv','walkingHR','cardioRecovery','bp_systolic','bp_diastolic','spo2','vo2max'] },
      { label: 'Metabolic',    keys: ['bloodGlucose','restingEnergy','activeEnergy'] },
      { label: 'Sleep',        keys: ['sleep','sleepAwake','sleepREM','sleepCore','sleepDeep','respRate','wristTemp'] },
      { label: 'Activity',     keys: ['exerciseTime','workoutTime','steps','walkRunDistance','walkingSteadiness','walkingSpeed','stepLength'] },
    ].map(group => {
      const groupHasData = group.keys.some(k => S.healthData.some(d => d.type === k))
      return `<div style="margin-bottom:8px">
        <div style="font-size:10px;font-weight:700;color:var(--text4);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px">${group.label}</div>
        <div style="display:flex;flex-wrap:wrap;gap:4px">
          ${group.keys.map(key => {
            const v = HEALTH_TYPES[key]
            const count = S.healthData.filter(d=>d.type===key).length
            return `<button class="health-metric-tab ${S.healthMetric===key?'active':''}" onclick="S.healthMetric='${key}';render()">
              ${v.label}${count ? ` <span style="opacity:.6;font-size:10px">${count}</span>` : ''}
            </button>`
          }).join('')}
        </div>
      </div>`
    }).join('')}

    <!-- Date range pickers -->
    <div class="card" style="margin-bottom:16px;padding:0">
      <div class="card-body" style="display:flex;flex-direction:column;gap:12px">
        <div class="row" style="gap:12px;flex-wrap:wrap;align-items:flex-end">
          <div class="form-group" style="margin:0;flex:1;min-width:140px">
            <label class="form-label">Start Date</label>
            <input class="form-input" type="date" value="${S.healthStart}"
              oninput="S.healthStart=this.value;render()">
          </div>
          <div class="form-group" style="margin:0;flex:1;min-width:140px">
            <label class="form-label">End Date</label>
            <input class="form-input" type="date" value="${S.healthEnd}"
              oninput="S.healthEnd=this.value;render()">
          </div>
          <button class="btn btn-ghost btn-sm" onclick="S.healthStart='';S.healthEnd='';render()" style="flex-shrink:0">Clear</button>
        </div>
        <div class="filter-bar" style="margin:0">
          <span style="font-size:11px;font-weight:700;color:var(--text4)">Quick:</span>
          ${presets.map(([d,l]) => {
            const active = isPresetActive(d)
            return `<button class="filter-btn ${active?'active':''}" onclick="${d
              ? `S.healthStart=localDateOffset(-${d});S.healthEnd='';render()`
              : `S.healthStart='';S.healthEnd='';render()`
            }">${l}</button>`
          }).join('')}
        </div>
      </div>
    </div>

    <!-- Stats row -->
    ${latest ? `
    <div class="stat-grid" style="margin-bottom:16px">
      <div class="stat-card">
        <div class="stat-value" style="font-size:20px">${(+latest.value).toFixed(1)} <span style="font-size:12px;color:var(--text3)">${HEALTH_TYPES[metric]?.unit}</span></div>
        <div class="stat-label">Latest (${fmt(latest.date)})</div>
      </div>
      ${trend !== null ? `<div class="stat-card">
        <div class="stat-value" style="font-size:20px;color:${+trend>0?'var(--red)':'var(--green)'}">${+trend>0?'+':''}${trend}</div>
        <div class="stat-label">Change in period</div>
      </div>` : ''}
      <div class="stat-card">
        <div class="stat-value" style="font-size:20px">${metricData.length}</div>
        <div class="stat-label">Data points</div>
      </div>
    </div>` : `<div style="color:var(--text4);font-size:13px;margin-bottom:16px">No ${HEALTH_TYPES[metric]?.label} data in this range.</div>`}

    <!-- Chart -->
    ${series[0]?.points.length > 1 ? `
    <div class="chart-wrap">
      <div class="chart-title">${HEALTH_TYPES[metric]?.label} over time</div>
      ${svgLineChart(series, { height: 200 })}
    </div>` : ''}
    `}
  </div>`
}

function var_blue() { return '#0072B2' }

async function importHealthXML() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.xml'
  input.onchange = async e => {
    const file = e.target.files[0]
    if (!file) return
    const sizeMB = (file.size / 1024 / 1024).toFixed(0)
    showToast(`Processing ${sizeMB}MB file — this will take a few minutes…`, 120000)
    await new Promise(r => setTimeout(r, 150))
    try {
      await parseHealthXMLStream(file)
    } catch(err) {
      showToast('Parse error: ' + err.message)
      console.error(err)
    }
  }
  input.click()
}

async function parseHealthXMLStream(file) {
  const hkMap = {}
  Object.entries(HEALTH_TYPES).forEach(([key, v]) => { if (v.hk) hkMap[v.hk] = key })

  const newRecords = []
  const sleepBySource = {}
  const dailySourceSums = {}
  const activitySummaries = {}
  const dailySamples = { heartRate:{}, walkingSpeed:{}, stepLength:{} }
  const dailyLatest = {}
  let added = 0
  let recordCount = 0

  // Fast inline attribute extractor using indexOf
  function getAttr(tag, name) {
    const needle = name + '="'
    const i = tag.indexOf(needle)
    if (i === -1) return ''
    const j = tag.indexOf('"', i + needle.length)
    return j === -1 ? '' : tag.slice(i + needle.length, j)
  }

  function addSourceSum(type, date, source, value) {
    if (!dailySourceSums[type]) dailySourceSums[type] = {}
    if (!dailySourceSums[type][date]) dailySourceSums[type][date] = {}
    const sourceKey = source || 'Unknown source'
    dailySourceSums[type][date][sourceKey] = (dailySourceSums[type][date][sourceKey] || 0) + value
  }

  function processTag(tag) {
    const type = getAttr(tag, 'type')
    const mapped = hkMap[type]
    if (!mapped) return

    const rawDate = getAttr(tag, 'startDate') || getAttr(tag, 'creationDate')
    const dm = rawDate.match(/(\d{4}-\d{2}-\d{2})/)
    if (!dm) return
    const date = dm[1]
    const source = getAttr(tag, 'sourceName') || 'Unknown source'

    if (mapped === 'sleep') {
      const startRaw = getAttr(tag, 'startDate')
      const endRaw   = getAttr(tag, 'endDate')
      if (startRaw && endRaw) {
        const dur = (new Date(endRaw) - new Date(startRaw)) / 3600000
        if (dur > 0 && dur < 24) {
          const wakeDate = (endRaw.match(/(\d{4}-\d{2}-\d{2})/) || [])[1] || date
          if (!sleepBySource[wakeDate]) sleepBySource[wakeDate] = {}
          if (!sleepBySource[wakeDate][source]) sleepBySource[wakeDate][source] = { sleep:0, sleepAwake:0, sleepREM:0, sleepCore:0, sleepDeep:0, staged:0 }
          const bucket = sleepBySource[wakeDate][source]
          const value = getAttr(tag, 'value')
          const stage = value.includes('Awake') ? 'sleepAwake' : value.includes('REM') ? 'sleepREM' : value.includes('Core') ? 'sleepCore' : value.includes('Deep') ? 'sleepDeep' : ''
          if (stage) { bucket[stage] += dur; bucket.staged++ }
          if ((stage && stage !== 'sleepAwake') || value.includes('AsleepUnspecified')) bucket.sleep += dur
        }
      }
      return
    }

    // Steps: sum per day
    if (mapped === 'steps') {
      const value = getAttr(tag, 'value')
      if (!value || isNaN(+value)) return
      addSourceSum('steps', date, source, Math.round(+value))
      return
    }

    const value = getAttr(tag, 'value')
    if (!value || isNaN(+value)) return

    const unit = getAttr(tag, 'unit')
    let val = +value

    // Unit conversions
    if ((mapped === 'weight' || mapped === 'leanBodyMass') && unit === 'kg') val *= 2.20462
    if (mapped === 'bodyFat') val = +(val > 1 ? val : val * 100).toFixed(2)
    if (mapped === 'restingEnergy' || mapped === 'activeEnergy') {
      if (unit === 'kJ') val *= 0.239006
      addSourceSum(mapped, date, source, val)
      added++; return
    }
    // SpO2: stored as 0.0–1.0, convert to %
    if (mapped === 'spo2') val = +(val > 1 ? val : val * 100).toFixed(1)
    // Blood glucose: mmol/L → mg/dL
    if (mapped === 'bloodGlucose' && (unit === 'mmol/L' || unit.includes('mmol'))) val = +(val * 18.0182).toFixed(0)
    // Wrist temp: °C → °F
    if (mapped === 'wristTemp' && unit === '°C') val = +(val * 9/5 + 32).toFixed(2)
    if (mapped === 'walkRunDistance') {
      if (unit === 'km') val *= 0.621371
      else if (unit === 'm') val *= 0.000621371
    }
    if (mapped === 'walkingSpeed') {
      if (unit === 'm/s') val *= 2.23694
      else if (unit === 'km/hr') val *= 0.621371
    }
    if (mapped === 'stepLength') {
      if (unit === 'cm') val *= 0.393701
      else if (unit === 'm') val *= 39.3701
    }
    if (mapped === 'walkingSteadiness') val = +(val > 1 ? val : val * 100).toFixed(1)
    if (mapped === 'exerciseTime') {
      if (unit === 'hr') val *= 60
      addSourceSum('exerciseTime', date, source, val)
      added++; return
    }
    if (mapped === 'walkRunDistance') {
      addSourceSum('walkRunDistance', date, source, val)
      added++; return
    }
    if (dailySamples[mapped]) {
      ;(dailySamples[mapped][date] = dailySamples[mapped][date] || []).push(val)
      added++; return
    }
    // VO2Max: stored as mL/min·kg, keep as-is
    // Respiratory rate: breaths/min, keep as-is

    if (!dailyLatest[mapped]) dailyLatest[mapped] = {}
    const sampleAt = getAttr(tag, 'endDate') || getAttr(tag, 'startDate') || getAttr(tag, 'creationDate') || date
    const prior = dailyLatest[mapped][date]
    if (!prior || sampleAt >= prior.sampleAt) dailyLatest[mapped][date] = { value:val, sampleAt, source }
    added++
  }

  function processWorkoutTag(tag) {
    const rawDate = getAttr(tag, 'startDate') || getAttr(tag, 'creationDate')
    const dm = rawDate.match(/(\d{4}-\d{2}-\d{2})/)
    if (!dm) return
    let duration = +getAttr(tag, 'duration')
    if (!Number.isFinite(duration) || duration <= 0) return
    const unit = getAttr(tag, 'durationUnit')
    if (unit === 'hr') duration *= 60
    else if (unit === 's') duration /= 60
    addSourceSum('workoutTime', dm[1], getAttr(tag, 'sourceName') || 'Workout', duration)
    added++
  }

  function processActivitySummaryTag(tag) {
    const rawDate = getAttr(tag, 'dateComponents')
    const dm = rawDate.match(/(\d{4}-\d{2}-\d{2})/)
    if (!dm) return
    const summary = activitySummaries[dm[1]] = activitySummaries[dm[1]] || {}
    const activeRaw = getAttr(tag, 'activeEnergyBurned')
    const exerciseRaw = getAttr(tag, 'appleExerciseTime')
    if (activeRaw !== '' && Number.isFinite(+activeRaw)) summary.activeEnergy = +activeRaw
    if (exerciseRaw !== '' && Number.isFinite(+exerciseRaw)) summary.exerciseTime = +exerciseRaw
  }

  // Stream the file in 4MB chunks — never loads whole file into memory
  const decoder = new TextDecoder('utf-8')
  const stream  = file.stream()
  const reader  = stream.getReader()

  let buffer = ''        // accumulates text across chunk boundaries
  let yieldEvery = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    // Decode this chunk and add to buffer
    buffer += decoder.decode(value, { stream: true })

    // Process all complete <Record .../> or <Record ...> tags in buffer
    let searchFrom = 0
    while (true) {
      const recordStart = buffer.indexOf('<Record', searchFrom)
      const workoutStart = buffer.indexOf('<Workout', searchFrom)
      const summaryStart = buffer.indexOf('<ActivitySummary', searchFrom)
      const starts = [recordStart, workoutStart, summaryStart].filter(index => index !== -1)
      if (!starts.length) break
      const start = Math.min(...starts)

      const end = buffer.indexOf('>', start)
      if (end === -1) break  // tag not complete yet — wait for next chunk

      const tag = buffer.slice(start, end + 1)
      if (tag.startsWith('<Workout')) processWorkoutTag(tag)
      else if (tag.startsWith('<ActivitySummary')) processActivitySummaryTag(tag)
      else processTag(tag)
      searchFrom = end + 1
      recordCount++

      // Yield to browser every 2000 records to keep UI alive
      yieldEvery++
      if (yieldEvery % 2000 === 0) {
        await new Promise(r => setTimeout(r, 0))
      }
    }

    // Discard every complete tag. Retaining bytes before searchFrom causes the
    // same Apple Health records to be read again with every stream chunk.
    if (searchFrom > 0) buffer = buffer.slice(searchFrom)
  }

  // Flush remaining buffer
  const tail = decoder.decode()
  if (tail) {
    buffer += tail
    let searchFrom = 0
    while (true) {
      const recordStart = buffer.indexOf('<Record', searchFrom)
      const workoutStart = buffer.indexOf('<Workout', searchFrom)
      const summaryStart = buffer.indexOf('<ActivitySummary', searchFrom)
      const starts = [recordStart, workoutStart, summaryStart].filter(index => index !== -1)
      if (!starts.length) break
      const start = Math.min(...starts)
      const end = buffer.indexOf('>', start)
      if (end === -1) break
      const tag = buffer.slice(start, end + 1)
      if (tag.startsWith('<Workout')) processWorkoutTag(tag)
      else if (tag.startsWith('<ActivitySummary')) processActivitySummaryTag(tag)
      else processTag(tag)
      searchFrom = end + 1
    }
  }

  const importedAt = new Date().toISOString()
  function addDailyRecord(date, type, value, sourceName) {
    if (!Number.isFinite(value)) return
    newRecords.push({ id:'h_'+uid(), date, type, value:+value.toFixed(2), unit:HEALTH_TYPES[type].unit, source:'apple-health', sourceName:sourceName||'', importedAt })
  }

  // Sleep can be exported by several apps for the same night. Prefer the
  // source with staged data, then the source with the largest asleep duration.
  Object.entries(sleepBySource).forEach(([date, sources]) => {
    const candidates = Object.entries(sources).filter(([,bucket]) => bucket.sleep > 0).sort((a,b) => (b[1].staged-a[1].staged) || (b[1].sleep-a[1].sleep))
    if (!candidates.length) return
    const [sourceName, bucket] = candidates[0]
    ;['sleep','sleepAwake','sleepREM','sleepCore','sleepDeep'].forEach(type => { if (bucket[type] > 0) addDailyRecord(date, type, bucket[type], sourceName) })
  })

  // Additive values commonly overlap between iPhone, Watch, and third-party
  // apps. Select the strongest daily source rather than adding duplicates.
  Object.entries(dailySourceSums).forEach(([type, byDate]) => Object.entries(byDate).forEach(([date, sources]) => {
    const best = Object.entries(sources).sort((a,b) => b[1]-a[1])[0]
    let value = best ? best[1] : 0
    let sourceName = best ? best[0] : ''
    if (activitySummaries[date] && Number.isFinite(activitySummaries[date][type])) { value = activitySummaries[date][type]; sourceName = 'Apple Activity Summary' }
    addDailyRecord(date, type, value, sourceName)
  }))
  Object.entries(activitySummaries).forEach(([date, summary]) => Object.entries(summary).forEach(([type, value]) => {
    if (!(dailySourceSums[type] && dailySourceSums[type][date])) addDailyRecord(date, type, value, 'Apple Activity Summary')
  }))
  Object.entries(dailySamples).forEach(([type, byDate]) => Object.entries(byDate).forEach(([date, values]) => {
    const average = values.reduce((sum, value) => sum + value, 0) / values.length
    addDailyRecord(date, type, average, 'Apple Health daily average')
  }))
  Object.entries(dailyLatest).forEach(([type, byDate]) => Object.entries(byDate).forEach(([date, sample]) => addDailyRecord(date, type, sample.value, sample.source)))

  // Re-imported Apple values are authoritative for the dates and metrics they
  // contain, allowing a corrected import to replace earlier inflated values.
  const importedKeys = new Set(newRecords.map(record => `${record.type}|${record.date}`))
  S.healthData = S.healthData.filter(record => !importedKeys.has(`${record.type}|${record.date}`)).concat(newRecords).sort((a,b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type))

  save(); render()
  showToast(`Imported ${newRecords.length.toLocaleString()} daily health values from ${recordCount.toLocaleString()} Apple Health records`)
}

async function clearHealthData() {
  if (!await asyncConfirm('Delete all health data?\n\nThis cannot be undone.')) return
  S.healthData = []
  save(); render(); showToast('Health data cleared')
}


// ══════════════════════════════════════════════════════
// RENDER: HEALTH INFO (documents tagged as health-related)
// ══════════════════════════════════════════════════════
function renderHealthDocsTab() {
  const allDocs = S.documents || []
  const healthDocs = allDocs.filter(d => d.type === 'health')
  return `<div class="tab-content">
    <div class="row" style="align-items:center;margin-bottom:16px">
      <h2 style="font-size:20px;font-weight:700;margin:0">Health Information</h2>
      <div class="spacer"></div>
      <span style="font-size:13px;color:var(--text4)">${healthDocs.length} document${healthDocs.length!==1?'s':''}</span>
    </div>

    ${healthDocs.length === 0 ? `
    <div class="card">
      <div class="empty-state" style="padding:48px">
        <div class="empty-icon">📋</div>
        <p>No health documents yet</p>
        <span>Documents with type "Health" will appear here. Add them in Documents → Import.</span>
      </div>
    </div>` : `
    <div style="display:flex;flex-direction:column;gap:8px">
      ${healthDocs.map(d => `
      <div class="card" style="padding:12px 14px">
        <div class="row" style="align-items:center;gap:10px">
          <span style="font-size:14px">📄</span>
          <div style="flex:1;min-width:0">
            <div style="font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(d.name||d.originalName||'Untitled')}</div>
            ${d.notes ? `<div style="font-size:12px;color:var(--text4);margin-top:2px">${esc(d.notes.substring(0,120))}${d.notes.length>120?'…':''}</div>` : ''}
            <div style="font-size:11px;color:var(--text4);margin-top:2px">${d.addedAt ? d.addedAt.slice(0,10) : ''}</div>
          </div>
          ${d.filePath && isElectron ? `<button class="btn btn-ghost btn-xs" onclick="window.api&&window.api.openExternal('file://'+encodeURI('${esc(d.filePath)}'))" title="Open file">Open</button>` : ''}
          <button class="btn btn-ghost btn-xs" onclick="setTab('docs')">View in Docs</button>
        </div>
      </div>`).join('')}
    </div>`}
  </div>`
}

function collectLabMarkers() {
  const rows = document.querySelectorAll('#lab-marker-rows > div, #lab-marker-rows > .form-row')
  const markers = []
  rows.forEach((row, i) => {
    const name = document.getElementById(`lm-name-${i}`)?.value?.trim()
    const value = document.getElementById(`lm-val-${i}`)?.value?.trim()
    if (!name && !value) return
    markers.push({
      id: uid(), name: name||'',
      value: value||'', unit: document.getElementById(`lm-unit-${i}`)?.value?.trim()||'',
      refLow: document.getElementById(`lm-lo-${i}`)?.value?.trim()||'',
      refHigh: document.getElementById(`lm-hi-${i}`)?.value?.trim()||''
    })
  })
  return markers
}

function saveAddLabPanel() {
  const panelName = document.getElementById('f-panel-name')?.value?.trim()
  const date = document.getElementById('f-panel-date')?.value
  if (!panelName || !date) { showToast('Panel name and date are required'); return }
  const markers = collectLabMarkers()
  if (!markers.length) { showToast('Add at least one marker'); return }
  const userId = document.getElementById('f-panel-user')?.value || ''
  S.labResults.push({ id: 'lr_'+uid(), panelName, date, userId, markers })
  save(); closeModal(); render()
  showToast('Lab panel added')
}

function saveEditLabPanel(panelId) {
  const panel = S.labResults.find(p => p.id === panelId)
  if (!panel) return
  const panelName = document.getElementById('f-panel-name')?.value?.trim()
  const date = document.getElementById('f-panel-date')?.value
  if (!panelName || !date) { showToast('Panel name and date are required'); return }
  panel.panelName = panelName
  panel.date = date
  panel.userId = document.getElementById('f-panel-user')?.value || ''
  panel.markers = collectLabMarkers()
  save(); closeModal(); render()
  showToast('Lab panel updated')
}

async function deleteLabPanel(panelId) {
  if (!await asyncConfirm('Delete this lab panel? This cannot be undone.')) return
  S.labResults = S.labResults.filter(function(p) { return p.id !== panelId })
  await save()
  showToast('Lab panel deleted')
  render()
}

function renderLabResultsTab() {
  const activeUsers = (S.users||[]).filter(u => u.active)
  const hasMultipleUsers = activeUsers.length > 1
  // Filter by user if selected
  const filteredResults = S._labUserFilter && S._labUserFilter !== 'all'
    ? S.labResults.filter(r => r.userId === S._labUserFilter)
    : S.labResults
  const sorted = [...filteredResults].sort((a,b) => b.date.localeCompare(a.date))

  // Per-marker history for trending
  const markerHistory = {}
  filteredResults.forEach(panel => {
    panel.markers?.forEach(m => {
      if (!markerHistory[m.name]) markerHistory[m.name] = []
      markerHistory[m.name].push({ date: panel.date, value: +m.value, unit: m.unit, refLow: +m.refLow||null, refHigh: +m.refHigh||null })
    })
  })
  Object.values(markerHistory).forEach(arr => arr.sort((a,b) => a.date.localeCompare(b.date)))

  return `
  <div style="animation:fadeIn .25s ease">
    <div class="row" style="margin-bottom:18px;flex-wrap:wrap;gap:10px">
      <h2 style="font-size:20px;font-weight:700">Lab Results</h2>
      <div class="spacer"></div>
      ${hasMultipleUsers ? `<select class="form-input" style="width:auto;font-size:13px" onchange="S._labUserFilter=this.value;render()">
        <option value="all" ${(S._labUserFilter||'all')==='all'?'selected':''}>All Users</option>
        ${activeUsers.map(u => `<option value="${u.id}" ${S._labUserFilter===u.id?'selected':''}>${esc(u.name)}</option>`).join('')}
      </select>` : ''}
      <button class="btn btn-secondary" onclick="openModal('aiLabImport')" style="margin-right:8px">🤖 AI Import</button>
      <button class="btn btn-primary" onclick="openModal('addLabPanel')">${ICONS.plus} Add Panel</button>
    </div>

    ${sorted.length === 0 ? `
    <div class="card">
      <div class="empty-state" style="padding:48px">
        <div class="empty-icon">🧪</div>
        <p>No lab results yet</p>
        <span>Add your blood test results to track markers like IGF-1, testosterone, ApoB over time</span>
      </div>
    </div>` : `

    <!-- Marker trends (markers with 2+ readings) -->
    ${Object.entries(markerHistory).filter(([,arr])=>arr.length>1).length ? `
    <div class="chart-wrap" style="margin-bottom:16px">
      <div class="chart-title">Marker Trends</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px">
        ${Object.entries(markerHistory).filter(([,arr])=>arr.length>1).map(([name,arr]) => {
          const latest = arr[arr.length-1]
          const prev   = arr[arr.length-2]
          const delta  = latest.value - prev.value
          const inRange = (!latest.refLow || latest.value >= latest.refLow) && (!latest.refHigh || latest.value <= latest.refHigh)
          const color  = !latest.refLow && !latest.refHigh ? 'var(--blue)' : inRange ? 'var(--green)' : 'var(--red)'
          return `<div style="background:var(--bg2);border:1px solid var(--border);border-radius:var(--r2);padding:10px 14px;min-width:140px">
            <div style="font-size:11px;color:var(--text4);font-weight:700;text-transform:uppercase">${esc(name)}</div>
            <div style="font-size:20px;font-weight:700;color:${color}">${latest.value} <span style="font-size:11px;color:var(--text3)">${esc(latest.unit||'')}</span></div>
            <div style="font-size:11px;color:${delta>0?'var(--red)':'var(--green)'}">
              ${delta>0?'▲':'▼'} ${Math.abs(delta).toFixed(1)} from prev
            </div>
          </div>`
        }).join('')}
      </div>
    </div>` : ''}

    <!-- Panels -->
    ${sorted.map(panel => {
      const outOfRange = panel.markers?.filter(m =>
        (m.refLow && +m.value < +m.refLow) || (m.refHigh && +m.value > +m.refHigh)
      ).length || 0
      return `
      <div class="card" style="margin-bottom:12px">
        <div class="card-header">
          <div>
            <div class="card-title">${esc(panel.panelName)}</div>
            <div style="font-size:12px;color:var(--text3)">${fmt(panel.date)} · ${panel.markers?.length||0} markers</div>
          </div>
          <div class="row" style="gap:8px">
            ${outOfRange ? `<span class="badge badge-red">${outOfRange} out of range</span>` : '<span class="badge badge-green">All in range</span>'}
            <button class="btn btn-ghost btn-xs" onclick="openModal('editLabPanel',{panelId:'${panel.id}'})">${ICONS.edit}</button>
            <button class="btn btn-danger btn-xs" onclick="deleteLabPanel('${panel.id}')">${ICONS.trash}</button>
          </div>
        </div>
        <div class="card-body" style="padding:0">
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <thead>
              <tr style="border-bottom:1px solid var(--border)">
                <th style="padding:8px 16px;text-align:left;font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase">Marker</th>
                <th style="padding:8px 16px;text-align:right;font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase">Value</th>
                <th style="padding:8px 16px;text-align:right;font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase">Reference</th>
                <th style="padding:8px 16px;text-align:center;font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              ${(panel.markers||[]).map(m => {
                const low = m.refLow ? +m.refLow : null
                const high = m.refHigh ? +m.refHigh : null
                const val  = +m.value
                const outLow  = low !== null && val < low
                const outHigh = high !== null && val > high
                const out = outLow || outHigh
                const color = (low === null && high === null) ? 'var(--text)' : out ? 'var(--red)' : 'var(--green)'
                return `<tr style="border-bottom:1px solid var(--border2)">
                  <td style="padding:8px 16px;font-weight:500">${esc(m.name)}</td>
                  <td style="padding:8px 16px;text-align:right;font-weight:700;color:${color}">${esc(m.value)} ${esc(m.unit||'')}</td>
                  <td style="padding:8px 16px;text-align:right;color:var(--text3)">${low||high ? (low||'')+'–'+(high||'') : '—'}</td>
                  <td style="padding:8px 16px;text-align:center">${
                    low===null&&high===null ? '<span style="color:var(--text4)">—</span>' :
                    out ? '<span style="color:var(--red);font-weight:700">⚠ Out</span>' :
                    '<span style="color:var(--green)">✓</span>'
                  }</td>
                </tr>`
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>`
    }).join('')}`}
  </div>`
}


// ── Lab Trend Charts ──
function renderLabTrend(markerName) {
  const panels = (S.labResults||[]).sort((a,b) => a.date.localeCompare(b.date))
  const points = []
  for (const panel of panels) {
    for (const m of (panel.markers||[])) {
      // BN16: match on normalized name
      const normName = typeof normalizeMarkerName === 'function' ? normalizeMarkerName(m.name) : m.name
      if (m.name === markerName || normName === markerName) {
        points.push({ date: panel.date, value: parseFloat(m.value), refLow: parseFloat(m.refLow||0), refHigh: parseFloat(m.refHigh||999) })
      }
    }
  }
  if (points.length < 2) return '<div style="color:var(--text4);font-size:12px;padding:8px">Need 2+ data points for trend</div>'

  const min = Math.min(...points.map(p => p.value)) * 0.9
  const max = Math.max(...points.map(p => p.value)) * 1.1
  const w = 600, h = 200, pad = 40
  const xStep = (w - pad*2) / (points.length - 1)

  let pathD = ''
  let dots = ''
  let refBand = ''

  // Reference range band
  if (points[0].refLow > 0 && points[0].refHigh < 900) {
    const yLow = pad + (max - points[0].refHigh) / (max - min) * (h - pad*2)
    const yHigh = pad + (max - points[0].refLow) / (max - min) * (h - pad*2)
    refBand = `<rect x="${pad}" y="${yLow}" width="${w-pad*2}" height="${yHigh-yLow}" fill="var(--greenL)" opacity="0.3" rx="2"/>`
  }

  points.forEach((p, i) => {
    const x = pad + i * xStep
    const y = pad + (max - p.value) / (max - min) * (h - pad*2)
    pathD += (i === 0 ? 'M' : 'L') + `${x},${y}`
    const inRange = p.value >= p.refLow && p.value <= p.refHigh
    dots += `<circle cx="${x}" cy="${y}" r="4" fill="${inRange ? 'var(--green)' : 'var(--red)'}" stroke="var(--bg2)" stroke-width="2"/>`
    dots += `<text x="${x}" y="${y-10}" text-anchor="middle" font-size="10" fill="var(--text3)">${p.value}</text>`
    if (i % Math.ceil(points.length/6) === 0 || i === points.length-1)
      dots += `<text x="${x}" y="${h-5}" text-anchor="middle" font-size="9" fill="var(--text4)">${p.date.slice(5)}</text>`
  })

  // BN14: Overlay PROTOCOL windows (not just cycles)
  let protocolOverlay = ''
  for (const proto of (S.protocols||[])) {
    if (proto.status !== 'active' && proto.status !== 'completed') continue
    if (!proto.startDate || !proto.endDate) continue
    const startIdx = points.findIndex(p => p.date >= proto.startDate)
    const endIdx = points.findIndex(p => p.date >= proto.endDate)
    if (startIdx >= 0) {
      const x1 = pad + startIdx * xStep
      const x2 = endIdx >= 0 ? pad + endIdx * xStep : w - pad
      protocolOverlay += `<rect x="${x1}" y="${pad}" width="${Math.max(2,x2-x1)}" height="${h-pad*2}" fill="var(--accentL)" opacity="0.12" rx="2"/>`
      protocolOverlay += `<text x="${(x1+x2)/2}" y="${pad-4}" text-anchor="middle" font-size="8" fill="var(--accent)">${esc((proto.name||proto.peptideName||'').slice(0,15))}</text>`
    }
  }
  // Also overlay cycles for backward compat
  let cycleOverlay = ''
  for (const ct of (S.cycleTimelines||[])) {
    for (const comp of (ct.compounds||[])) {
      const entries = (comp.entries||[]).sort((a,b) => a.date.localeCompare(b.date))
      if (entries.length > 0) {
        const startDate = entries[0].date
        const endDate = entries[entries.length-1].date
        const startIdx = points.findIndex(p => p.date >= startDate)
        const endIdx = points.findIndex(p => p.date >= endDate)
        if (startIdx >= 0) {
          const x1 = pad + startIdx * xStep
          const x2 = endIdx >= 0 ? pad + endIdx * xStep : w - pad
          cycleOverlay += `<rect x="${x1}" y="${pad}" width="${x2-x1}" height="${h-pad*2}" fill="var(--amberL)" opacity="0.1" rx="2"/>`
          cycleOverlay += `<text x="${(x1+x2)/2}" y="${pad+12}" text-anchor="middle" font-size="7" fill="var(--amber)">${esc(comp.name)}</text>`
        }
      }
    }
  }

  return `<svg viewBox="0 0 ${w} ${h}" style="width:100%;max-height:220px">
    ${refBand}${protocolOverlay}${cycleOverlay}
    <path d="${pathD}" fill="none" stroke="var(--accent)" stroke-width="2"/>
    ${dots}
  </svg>`
}

// Get all unique marker names across lab results
function getAllLabMarkers() {
  const markers = new Set()
  for (const panel of (S.labResults||[])) {
    for (const m of (panel.markers||[])) {
      // BN16: normalize name if available
      const normName = typeof normalizeMarkerName === 'function' ? normalizeMarkerName(m.name) : m.name
      if (normName) markers.add(normName)
    }
  }
  return [...markers].sort()
}

// BN15: Lab Panel Templates — reusable marker group definitions
function getLabPanelTemplates() {
  // Panel templates stored as entries in labMarkerDictionary with category field
  // Group by category to form virtual panels
  var dict = S.labMarkerDictionary || []
  var cats = {}
  dict.forEach(function(d) {
    var cat = d.category || 'Uncategorized'
    if (!cats[cat]) cats[cat] = []
    cats[cat].push(d.canonicalName)
  })
  return cats
}

function checkPanelCompletion(panelCategory, labPanelId) {
  var templates = getLabPanelTemplates()
  var expectedMarkers = templates[panelCategory] || []
  if (expectedMarkers.length === 0) return null
  var panel = (S.labResults || []).find(function(p) { return p.id === labPanelId })
  if (!panel) return null
  var presentNames = (panel.markers || []).map(function(m) {
    return typeof normalizeMarkerName === 'function' ? normalizeMarkerName(m.name).toLowerCase() : m.name.toLowerCase()
  })
  var present = 0
  var missing = []
  expectedMarkers.forEach(function(name) {
    if (presentNames.indexOf(name.toLowerCase()) >= 0) present++
    else missing.push(name)
  })
  return { total: expectedMarkers.length, present: present, missing: missing, pct: Math.round(present / expectedMarkers.length * 100) }
}

// BN14: Marker selector for trend view
function renderMarkerSelector() {
  var markers = getAllLabMarkers()
  if (markers.length === 0) return ''
  var selected = (S.labTrendConfig || {}).selectedMarkers || []
  return '<div style="margin-bottom:12px">' +
    '<div style="font-size:11px;font-weight:700;color:var(--text4);text-transform:uppercase;margin-bottom:4px">Select Markers for Trend</div>' +
    '<div style="display:flex;flex-wrap:wrap;gap:4px">' +
    markers.map(function(m) {
      var isSel = selected.indexOf(m) >= 0
      return '<button class="btn btn-xs ' + (isSel ? 'btn-primary' : 'btn-secondary') + '" onclick="toggleTrendMarker(\'' + esc(m) + '\')">' + esc(m) + '</button>'
    }).join('') +
    '</div></div>'
}

function toggleTrendMarker(name) {
  if (!S.labTrendConfig) S.labTrendConfig = {}
  if (!S.labTrendConfig.selectedMarkers) S.labTrendConfig.selectedMarkers = []
  var idx = S.labTrendConfig.selectedMarkers.indexOf(name)
  if (idx >= 0) S.labTrendConfig.selectedMarkers.splice(idx, 1)
  else S.labTrendConfig.selectedMarkers.push(name)
  render()
}

export {
  HEALTH_TYPES,
  renderHealthTab,
  var_blue,
  importHealthXML,
  parseHealthXMLStream,
  clearHealthData,
  renderHealthDocsTab,
  collectLabMarkers,
  saveAddLabPanel,
  saveEditLabPanel,
  deleteLabPanel,
  renderLabResultsTab,
  renderLabTrend,
  getAllLabMarkers,
  getLabPanelTemplates,
  checkPanelCompletion,
  renderMarkerSelector,
  toggleTrendMarker
}
